import { useTheme } from '@mui/material';
import { indigo, lavender, palettes } from '@/theme/tokens';
import './mark.css';

/**
 * The URWANA compact mark: two eight-armed bursts, one indigo and one
 * lavender, offset across the diagonal. The paths are copied verbatim from the
 * owner-supplied artwork (`public/brand/uruanna-compact.svg`, viewBox
 * 427 × 430) — never redraw or re-cut them.
 *
 * `uw-a` is the indigo burst and `uw-b` the lavender one, both inside the
 * group `uw-g`; the class names are what `mark.css` animates.
 */
const VIEW = { w: 427, h: 430 } as const;

const BURST_A =
  'M284.947 6.79101C252.221 17.5114 203.291 20.8113 165.947 9.29101C181.979 57.6496 192.378 132.952 201.946 174.791L114.446 24.2054C97.5017 55.8515 72.7775 89.7592 33.9462 106.291C81.4271 136.611 145.282 168.453 179.322 195.791L10.9453 151.791C18.973 180.873 15.2206 229.055 2.945 264.791C46.9292 254.216 99.4467 242.291 158.216 229.504L30.9467 312.291C70.7504 331.557 92.1532 362.411 102.947 386.791C137.964 331.476 182.935 269.633 215.445 219.291C220.327 202.717 233.841 162.136 255.947 90.291C265.947 57.791 279.162 21.0904 284.947 6.79101Z';

const BURST_B =
  'M142 422.253C174.726 411.533 223.655 408.233 261 419.753C244.968 371.395 234.569 296.092 225.001 254.253L312.501 404.839C329.445 373.193 354.169 339.285 393.001 322.753C345.52 292.433 281.665 260.591 247.625 233.253L416.002 277.253C407.974 248.171 411.726 199.989 424.002 164.253C380.018 174.828 327.5 186.753 268.731 199.541L396 116.753C356.196 97.4873 334.794 66.6334 324 42.2532C288.983 97.568 244.012 159.411 211.502 209.753C206.62 226.327 193.106 266.908 171 338.753C161 371.253 147.785 407.954 142 422.253Z';

export type MarkTone = 'brand' | 'inverse' | 'paper';
export type MarkAnimation = 'spin' | 'breathe' | 'bloom' | 'drift';

export interface UruannaMarkProps {
  /** Height in px; the width follows the artwork's ratio. */
  size?: number;
  /**
   * `brand` follows the mode — indigo burst on a light ground, near-white on
   * the dark one. `inverse` is the white burst, for an indigo or dark pane in
   * either mode. `paper` is always indigo: a printed sheet is white whatever
   * the screen's mode. The lavender burst is the same in all three.
   */
  tone?: MarkTone;
  /**
   * The logo is the one thing in the product that moves (owner's call,
   * 2026-10-21). `spin` is the spinner, `breathe` a page waiting on data,
   * `bloom` the entrance, `drift` the sign-in pane's background mark (bloom
   * once into the assembled mark, then the whole mark turns as one, 140s a
   * turn). All are still under
   * `prefers-reduced-motion`.
   */
  animate?: MarkAnimation;
  /** Hide from assistive technology when the surrounding element names it. */
  decorative?: boolean;
  /** Accessible name when not decorative. */
  label?: string;
}

export function UruannaMark({
  size = 28,
  tone = 'brand',
  animate,
  decorative,
  label = 'URWANA',
}: UruannaMarkProps) {
  const theme = useTheme();
  const burstA =
    tone === 'inverse'
      ? palettes.light.onPrimary
      : tone === 'paper' || theme.palette.mode === 'light'
        ? indigo.brand
        : palettes.dark.ink;

  return (
    <svg
      className={animate ? `uw-${animate}` : undefined}
      viewBox={`0 0 ${VIEW.w} ${VIEW.h}`}
      width={(size * VIEW.w) / VIEW.h}
      height={size}
      fill="none"
      style={{ display: 'block', flexShrink: 0, overflow: 'visible' }}
      {...(decorative ? { 'aria-hidden': true } : { role: 'img', 'aria-label': label })}
    >
      {/* The pair as one object: `drift` turns the group, never the halves
          apart, so the mark stays assembled once the bloom has landed. */}
      <g className="uw-g">
        <path className="uw-b" d={BURST_B} fill={lavender[500]} />
        <path className="uw-a" d={BURST_A} fill={burstA} />
      </g>
    </svg>
  );
}
